import { type AdminSelf, isApiError } from '@saay/api-client';
import { IconTile, StatusBadge } from '@saay/blocks';
import { buttonVariants } from '@saay/shadcn/components/button';
import { SparklesIcon } from 'lucide-react';
import {
  formatCount,
  formatLocalDate,
  formatTimestamp,
  formatToman,
  formatTomanCompact,
  statusLabel,
  toPersianDigits,
} from '@saay/formatting';
import {
  Combobox,
  DateRangePicker,
  EmptyState,
  ErrorState,
  Icon,
  LinearProgress,
  LoadMore,
  MoneyField,
  NativeSelect,
  NumberField,
  PageHeader,
  Section,
  SegmentedControl,
  Skeleton,
  StatusChip,
  TextArea,
  TextField,
  useSearchInput,
  useToast,
} from '../kit.js';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, type ReactNode } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import type {
  CreditProgram,
  CreditProgramActivation,
  CreditProgramCreate,
  CreditProgramUpdate,
  ValidityPolicy,
} from '../api.js';
import { formatProgramCodeRevision } from '../display.js';
import { CommandDialog } from '../components/command-dialog.js';
import { normalizeAdminCode } from '../components/form.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { AdminListTable } from '../components/admin-list-table.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { ListSearchInput } from '../components/list-filters.js';
import { OPENED_FROM_LIST, onSheetOpenChange, useCloseSheet } from '../components/route-sheet.js';
import { OUTLINE_PILL } from '../components/surface-section.js';
import { creditProgramTargetingPath } from '../nav.js';
import { canUse, executesApprovalDirectly } from '../permissions.js';
import {
  type CreditProgramFilters,
  listStatus,
  programPath,
  setupKeys,
  useActiveOrganizations,
  useAdminSelf,
  useCreditPrograms,
} from '../queries.js';
import { reasonSchema, startOfBusinessDay, tehranToday } from '../validation.js';
import { FormReasonChips } from '../components/reason-field.js';

/**
 * `/credit-programs` (FTD §9.6, prototype «برنامه‌ها و اعتبار اضافی»): the S8 tiles, the program list
 * (C1), «برنامه جدید» (C2) and «تخصیص جدید» (the targeting sheet). A program's revisions, runs, and
 * commands are in its drawer. Targeting runs are one-shot (F-4): there are no standing rules.
 */

export interface CreditProgramsOutletContext {
  readonly self: AdminSelf;
}

/** «۲ ماه از تخصیص», «۱ مهر تا ۳۰ آبان», «بدون انقضا» (AC §4.7), from the stored policy. */
export function validityText(policy: ValidityPolicy): string {
  switch (policy.mode) {
    case 'DURATION_FROM_ASSIGNMENT':
      return `${toPersianDigits(policy.value)} ${policy.unit === 'MONTH' ? 'ماه' : 'روز'} از تخصیص`;
    case 'FIXED_WINDOW':
      return `${formatTimestamp(policy.validFrom, { withTime: false })} تا ${formatTimestamp(policy.validUntil, { withTime: false })}`;
    case 'NON_EXPIRING':
      return 'بدون انقضا';
    default:
      return statusLabel('validityMode', (policy as { mode: string }).mode);
  }
}

export function programTitle(program: Pick<CreditProgram, 'nameFa' | 'revision'>): string {
  return `${program.nameFa} · نسخه ${toPersianDigits(program.revision)}`;
}

export function useRefreshPrograms(programId?: string) {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: setupKeys.programs }),
      queryClient.invalidateQueries({ queryKey: setupKeys.summaries }),
      queryClient.invalidateQueries({ queryKey: setupKeys.approvals }),
      queryClient.invalidateQueries({ queryKey: setupKeys.organizations }),
      ...(programId === undefined
        ? []
        : [queryClient.invalidateQueries({ queryKey: setupKeys.program(programId) })]),
    ]);
  };
}

// ---------------------------------------------------------------------------------------------
// C2 create, C4 edit (DRAFT only)

const STATUS_RANK: Readonly<Record<CreditProgram['status'], number>> = {
  ACTIVE: 4,
  CLOSED: 0,
  DRAFT: 3,
  PENDING_APPROVAL: 2,
  SUPERSEDED: 1,
};

/** Keeps the latest open revision per `programCode` (ADM-50). */
export function collapseCreditProgramRows(rows: readonly CreditProgram[]): CreditProgram[] {
  const byCode = new Map<string, CreditProgram>();
  for (const row of rows) {
    const existing = byCode.get(row.programCode);
    byCode.set(row.programCode, existing === undefined ? row : pickCreditProgramRow(existing, row));
  }
  return [...byCode.values()];
}

function pickCreditProgramRow(left: CreditProgram, right: CreditProgram): CreditProgram {
  if (right.revision !== left.revision) {
    return right.revision > left.revision ? right : left;
  }
  return STATUS_RANK[right.status] >= STATUS_RANK[left.status] ? right : left;
}

export function programExposureLabel(program: CreditProgram): string {
  return `${formatTomanCompact(program.exposure.committedIrr)} از ${formatTomanCompact(program.totalExposureCapIrr)}`;
}

export function sanitizeProgramCode(value: string): string {
  return normalizeAdminCode(value).replace(/[^A-Z0-9_-]/g, '');
}

function organizationComboboxOptions(
  orgs: readonly { displayNameFa: string; id: string; organizationCode: string }[],
) {
  const counts = new Map<string, number>();
  for (const org of orgs) {
    counts.set(org.displayNameFa, (counts.get(org.displayNameFa) ?? 0) + 1);
  }
  return [
    { label: 'همهٔ خریداران', value: '' },
    ...orgs.map((org) => ({
      label:
        (counts.get(org.displayNameFa) ?? 0) > 1
          ? `${org.displayNameFa} · ${org.organizationCode}`
          : org.displayNameFa,
      value: org.id,
    })),
  ];
}

function matchesProgramSearch(program: CreditProgram, raw: string): boolean {
  const q = raw.trim();
  if (q === '') return true;
  if (program.nameFa.includes(q)) return true;
  const upper = q.toUpperCase();
  return program.programCode.includes(upper);
}

const PROGRAM_CODE = /^[A-Z0-9][A-Z0-9_-]{1,63}$/;

export const programSchema = z
  .object({
    dedicatedOrganizationId: z.string(),
    defaultAmountIrr: z
      .number()
      .int()
      .nullable()
      .refine((value) => value !== null && value >= 1, 'مبلغ پیش‌فرض را وارد کنید.'),
    durationUnit: z.enum(['DAY', 'MONTH']),
    durationValue: z.string(),
    nameFa: z.string().trim().min(1, 'عنوان برنامه را وارد کنید.').max(200, 'حداکثر ۲۰۰ کاراکتر.'),
    programCode: z
      .string()
      .transform(normalizeAdminCode)
      .refine(
        (value) => PROGRAM_CODE.test(value),
        'حروف لاتین بزرگ، رقم، خط تیره و زیرخط؛ مثل WELCOME.',
      ),
    reason: reasonSchema(),
    totalExposureCapIrr: z
      .number()
      .int()
      .nullable()
      .refine((value) => value !== null && value >= 1, 'سقف کل تعهد را وارد کنید (D-36).'),
    validFrom: z.string(),
    validUntil: z.string(),
    validityMode: z.enum(['DURATION_FROM_ASSIGNMENT', 'FIXED_WINDOW', 'NON_EXPIRING']),
  })
  .superRefine((value, context) => {
    if (
      value.defaultAmountIrr !== null &&
      value.totalExposureCapIrr !== null &&
      value.totalExposureCapIrr < value.defaultAmountIrr
    ) {
      context.addIssue({
        code: 'custom',
        message: 'سقف کل تعهد نباید از مبلغ پیش‌فرض کمتر باشد.',
        path: ['totalExposureCapIrr'],
      });
    }
    if (value.validityMode === 'DURATION_FROM_ASSIGNMENT') {
      const amount = Number(value.durationValue);
      const max = value.durationUnit === 'MONTH' ? 120 : 3660;
      if (!/^\d+$/.test(value.durationValue) || amount < 1 || amount > max) {
        context.addIssue({
          code: 'custom',
          message: `عددی بین ۱ تا ${toPersianDigits(max)} وارد کنید.`,
          path: ['durationValue'],
        });
      }
    }
    if (value.validityMode === 'FIXED_WINDOW') {
      if (value.validFrom === '') {
        context.addIssue({
          code: 'custom',
          message: 'شروع بازه را وارد کنید.',
          path: ['validFrom'],
        });
      }
      if (value.validUntil === '' || value.validUntil <= tehranToday()) {
        context.addIssue({
          code: 'custom',
          message: 'پایان بازه باید در آینده باشد.',
          path: ['validUntil'],
        });
      } else if (value.validFrom !== '' && value.validUntil <= value.validFrom) {
        context.addIssue({
          code: 'custom',
          message: 'پایان بازه باید پس از شروع آن باشد.',
          path: ['validUntil'],
        });
      }
    }
  });
type ProgramValues = z.input<typeof programSchema>;

export function toValidity(values: z.output<typeof programSchema>): ValidityPolicy {
  switch (values.validityMode) {
    case 'DURATION_FROM_ASSIGNMENT':
      return {
        mode: 'DURATION_FROM_ASSIGNMENT',
        unit: values.durationUnit,
        value: Number(values.durationValue),
      };
    case 'FIXED_WINDOW':
      return {
        mode: 'FIXED_WINDOW',
        validFrom: startOfBusinessDay(values.validFrom),
        validUntil: startOfBusinessDay(values.validUntil),
      };
    default:
      return { mode: 'NON_EXPIRING' };
  }
}

/** A program's validity policy as form values (dates as the business date of the instant). */
export function validityValues(policy: ValidityPolicy | undefined) {
  const base = {
    durationUnit: 'MONTH' as const,
    durationValue: '2',
    validFrom: '',
    validUntil: '',
    validityMode: 'DURATION_FROM_ASSIGNMENT' as ProgramValues['validityMode'],
  };
  if (policy === undefined) return base;
  if (policy.mode === 'DURATION_FROM_ASSIGNMENT') {
    return { ...base, durationUnit: policy.unit, durationValue: String(policy.value) };
  }
  if (policy.mode === 'FIXED_WINDOW') {
    return {
      ...base,
      validFrom: tehranToday(new Date(policy.validFrom)),
      validUntil: tehranToday(new Date(policy.validUntil)),
      validityMode: 'FIXED_WINDOW' as const,
    };
  }
  return { ...base, validityMode: 'NON_EXPIRING' as const };
}

const PROGRAM_FIELDS = [
  'programCode',
  'nameFa',
  'dedicatedOrganizationId',
  'defaultAmountIrr',
  'totalExposureCapIrr',
  'reason',
];

/** The server's `validityPolicy.*` paths on the form fields the person filled in. */
const VALIDITY_ALIASES = {
  validityPolicy: 'validityMode',
  'validityPolicy.mode': 'validityMode',
  'validityPolicy.unit': 'durationUnit',
  'validityPolicy.validFrom': 'validFrom',
  'validityPolicy.validUntil': 'validUntil',
  'validityPolicy.value': 'durationValue',
} as const;

function ProgramFields({
  editing,
  form,
}: {
  readonly editing: CreditProgram | undefined;
  readonly form: UseFormReturn<ProgramValues, unknown, ProgramValues>;
}) {
  const organizations = useActiveOrganizations();
  const errors = form.formState.errors;
  const mode = form.watch('validityMode');
  const nameFa = form.watch('nameFa');
  const validFrom = form.watch('validFrom');
  const validUntil = form.watch('validUntil');
  const orgOptions = organizations.data ?? [];
  const dedicatedId = form.watch('dedicatedOrganizationId');
  const orgChoices = organizationComboboxOptions(orgOptions);
  useEffect(() => {
    if (editing !== undefined || organizations.status !== 'success' || orgOptions.length !== 1) {
      return;
    }
    if (dedicatedId === '') {
      form.setValue('dedicatedOrganizationId', orgOptions[0]!.id);
    }
  }, [dedicatedId, editing, form, orgOptions, organizations.status]);
  const suggestedReason = useRef('');
  useEffect(() => {
    if (editing !== undefined) return;
    const title = nameFa.trim();
    if (title === '') return;
    // Follow the title while the reason is still the suggestion; never overwrite the user's own text.
    const current = form.getValues('reason');
    const suggestion = `تعریف برنامهٔ ${title}`;
    if (current.trim() === '' || current === suggestedReason.current) {
      suggestedReason.current = suggestion;
      form.setValue('reason', suggestion);
    }
  }, [editing, form, nameFa]);
  return (
    <>
      <div className="admin-grid-2">
        <TextField
          error={errors.nameFa?.message}
          label="عنوان فارسی"
          {...form.register('nameFa')}
        />
        <Controller
          control={form.control}
          name="programCode"
          render={({ field, fieldState }) => (
            <TextField
              disabled={editing !== undefined}
              error={fieldState.error?.message}
              label="کد پایدار برنامه"
              ltr
              onChange={(event) => field.onChange(sanitizeProgramCode(event.target.value))}
              value={field.value}
            />
          )}
        />
        <Controller
          control={form.control}
          name="defaultAmountIrr"
          render={({ field, fieldState }) => (
            <MoneyField
              error={fieldState.error?.message}
              label="مبلغ پیش‌فرض هر نفر"
              onChangeRials={field.onChange}
              valueRials={field.value}
            />
          )}
        />
        <Controller
          control={form.control}
          name="totalExposureCapIrr"
          render={({ field, fieldState }) => (
            <MoneyField
              error={fieldState.error?.message}
              label="سقف کل تعهد برنامه"
              onChangeRials={field.onChange}
              valueRials={field.value}
            />
          )}
        />
        <Controller
          control={form.control}
          name="dedicatedOrganizationId"
          render={({ field, fieldState }) => (
            <Combobox
              error={fieldState.error?.message}
              label="سازمان اختصاصی"
              onChange={field.onChange}
              options={orgChoices}
              value={field.value === '' ? undefined : field.value}
            />
          )}
        />
      </div>
      <SegmentedControl
        label="اعتبار زمانی"
        onChange={(key) =>
          form.setValue('validityMode', key as ProgramValues['validityMode'], { shouldDirty: true })
        }
        options={(['DURATION_FROM_ASSIGNMENT', 'FIXED_WINDOW', 'NON_EXPIRING'] as const).map(
          (value) => ({ key: value, label: statusLabel('validityMode', value) }),
        )}
        value={mode}
      />
      {mode === 'DURATION_FROM_ASSIGNMENT' ? (
        <div className="admin-grid-2">
          <Controller
            control={form.control}
            name="durationValue"
            render={({ field, fieldState }) => {
              const unit = form.watch('durationUnit');
              const max = unit === 'MONTH' ? 120 : 3660;
              const parsed = field.value === '' ? null : Number(field.value.replace(/[^\d]/g, ''));
              const value = parsed === null || Number.isNaN(parsed) ? null : parsed;
              return (
                <NumberField
                  error={fieldState.error?.message}
                  label="مدت اعتبار"
                  max={max}
                  min={1}
                  onChange={(next) => {
                    field.onChange(next === null ? '' : String(next));
                  }}
                  suffix={unit === 'DAY' ? 'روز' : 'ماه'}
                  value={value}
                />
              );
            }}
          />
          <NativeSelect
            error={errors.durationUnit?.message}
            label="واحد مدت"
            options={[
              { label: 'ماه', value: 'MONTH' },
              { label: 'روز', value: 'DAY' },
            ]}
            {...form.register('durationUnit')}
          />
        </div>
      ) : null}
      {mode === 'FIXED_WINDOW' ? (
        <>
          <DateRangePicker
            error={errors.validFrom?.message ?? errors.validUntil?.message}
            label="بازهٔ اعتبار"
            onChange={(range) => {
              form.setValue('validFrom', range.start, { shouldDirty: true, shouldValidate: true });
              form.setValue('validUntil', range.end, { shouldDirty: true, shouldValidate: true });
            }}
            value={{ end: validUntil, start: validFrom }}
          />
          {validUntil === '' ? null : (
            <p className="admin-muted">{`تا پایان ${formatLocalDate(validUntil, 'long')}`}</p>
          )}
        </>
      ) : null}
      <TextArea
        error={errors.reason?.message}
        label="دلیل"
        maxLength={1000}
        {...form.register('reason')}
      />
      <FormReasonChips form={form} intent="credit." />
    </>
  );
}

/** C2 create (from «برنامه جدید», optionally dedicated) or C4 edit of a DRAFT. */
export function CreditProgramDialog({
  dedicatedOrganizationId,
  editing,
  onOpenChange,
}: {
  readonly dedicatedOrganizationId?: string;
  readonly editing?: CreditProgram;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshPrograms(editing?.id);
  const self = useAdminSelf().data;
  const activateDirect = self !== undefined && executesApprovalDirectly(self);
  const submitLabel = activateDirect ? 'ذخیره و فعال‌سازی' : 'ذخیره پیش‌نویس';
  const defaults: ProgramValues = {
    dedicatedOrganizationId: editing?.dedicatedOrganization?.id ?? dedicatedOrganizationId ?? '',
    defaultAmountIrr: editing?.defaultAmountIrr ?? null,
    nameFa: editing?.nameFa ?? '',
    programCode: editing?.programCode ?? '',
    reason: '',
    totalExposureCapIrr: editing?.totalExposureCapIrr ?? null,
    ...validityValues(editing?.validityPolicy),
  };
  const onError = (
    error: { readonly code: string; readonly messageFa: string },
    form: UseFormReturn<ProgramValues, unknown, ProgramValues>,
  ) => {
    if (error.code === 'CREDIT_PROGRAM_CODE_DUPLICATE') {
      form.setError('programCode', { message: error.messageFa, type: 'server' });
    }
  };
  if (editing === undefined) {
    return (
      <CommandDialog<ProgramValues, CreditProgramCreate, CreditProgram>
        defaultValues={defaults}
        eyebrow="تعریف نسخه تجاری"
        fieldAliases={VALIDITY_ALIASES}
        fields={PROGRAM_FIELDS}
        name="credit-program.create"
        onError={onError}
        onOpenChange={onOpenChange}
        onSuccess={async (result) => {
          await refresh();
          toast.success({
            confirmedBy: result,
            title: activateDirect
              ? `برنامهٔ ${result.data.nameFa} فعال شد`
              : `برنامهٔ ${result.data.nameFa} ساخته شد`,
          });
          void navigate(`/credit-programs/${result.data.id}`, { replace: true });
        }}
        schema={programSchema as never}
        send={async (client, body, intent) => {
          const created = await client.post<CreditProgram>('/admin/credit-programs', {
            body,
            intent,
          });
          if (!activateDirect || isApiError(created)) {
            return created;
          }
          const activationBody: CreditProgramActivation = {
            expectedVersion: created.data.version,
            reason: body.reason,
          };
          return client.post(programPath(created.data.id, '/activation'), {
            body: activationBody,
            intent,
          });
        }}
        size="wide"
        submitLabel={submitLabel}
        title="برنامه اعتبار اضافی"
        toBody={(values) => {
          const parsed = programSchema.parse(values);
          return {
            defaultAmountIrr: parsed.defaultAmountIrr as number,
            fundingSource: 'SAAY_CAPITAL',
            nameFa: parsed.nameFa,
            programCode: parsed.programCode,
            reason: parsed.reason,
            sourceType: 'SAAY',
            totalExposureCapIrr: parsed.totalExposureCapIrr as number,
            validityPolicy: toValidity(parsed),
            ...(parsed.dedicatedOrganizationId === ''
              ? {}
              : { dedicatedOrganizationId: parsed.dedicatedOrganizationId }),
          };
        }}
      >
        {(form) => <ProgramFields editing={undefined} form={form} />}
      </CommandDialog>
    );
  }
  return (
    <CommandDialog<ProgramValues, CreditProgramUpdate, CreditProgram>
      defaultValues={defaults}
      eyebrow={programTitle(editing)}
      fieldAliases={VALIDITY_ALIASES}
      fields={PROGRAM_FIELDS}
      guidance={programGuidance}
      name="credit-program.update"
      onError={async (error) => {
        if (
          error.code === 'RESOURCE_VERSION_CONFLICT' ||
          error.code === 'CREDIT_PROGRAM_IMMUTABLE'
        ) {
          await refresh();
        }
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          title: activateDirect ? 'برنامه فعال شد' : 'پیش‌نویس برنامه به‌روز شد',
        });
        onOpenChange(false);
      }}
      schema={programSchema as never}
      send={async (client, body, intent) => {
        const updated = await client.patch<CreditProgram>(programPath(editing.id), { body });
        if (!activateDirect || isApiError(updated)) {
          return updated;
        }
        const activationBody: CreditProgramActivation = {
          expectedVersion: updated.data.version,
          reason: body.reason,
        };
        return client.post(programPath(editing.id, '/activation'), {
          body: activationBody,
          intent,
        });
      }}
      size="wide"
      submitLabel={submitLabel}
      title="ویرایش برنامه"
      toBody={(values) => {
        const parsed = programSchema.parse(values);
        const changes: Partial<CreditProgramUpdate> = {};
        if (parsed.nameFa !== editing.nameFa) changes.nameFa = parsed.nameFa;
        if (parsed.defaultAmountIrr !== editing.defaultAmountIrr) {
          changes.defaultAmountIrr = parsed.defaultAmountIrr as number;
        }
        if (parsed.totalExposureCapIrr !== editing.totalExposureCapIrr) {
          changes.totalExposureCapIrr = parsed.totalExposureCapIrr as number;
        }
        const organizationId = editing.dedicatedOrganization?.id ?? '';
        if (parsed.dedicatedOrganizationId !== organizationId) {
          changes.dedicatedOrganizationId =
            parsed.dedicatedOrganizationId === '' ? null : parsed.dedicatedOrganizationId;
        }
        const validity = toValidity(parsed);
        if (JSON.stringify(validity) !== JSON.stringify(editing.validityPolicy)) {
          changes.validityPolicy = validity;
        }
        return Object.keys(changes).length === 0
          ? null
          : { ...changes, expectedVersion: editing.version, reason: parsed.reason };
      }}
    >
      {(form) => <ProgramFields editing={editing} form={form} />}
    </CommandDialog>
  );
}

/** The recovery line of a refused program command, by error code only (AC §18.9). */
export function programGuidance(error: unknown): ReactNode {
  if (typeof error !== 'object' || error === null || !('code' in error)) return undefined;
  const { code, details } = error as { code: string; details?: Record<string, unknown> };
  switch (code) {
    case 'CREDIT_PROGRAM_IMMUTABLE':
      return 'فقط پیش‌نویس قابل ویرایش است؛ برای تغییر شرایط، نسخهٔ جدید بسازید.';
    case 'CREDIT_PROGRAM_NOT_ACTIVE':
      return 'برنامه فعال نیست یا بازهٔ آن به پایان رسیده است.';
    case 'STATE_CONFLICT':
      return typeof details?.currentStatus === 'string'
        ? `وضعیت فعلی: ${statusLabel('creditProgramStatus', details.currentStatus)}. اطلاعات به‌روز شد.`
        : 'اطلاعات به‌روز شد.';
    case 'RESOURCE_VERSION_CONFLICT':
      return 'برنامه در این فاصله تغییر کرده است؛ اطلاعات به‌روز شد.';
    default:
      return undefined;
  }
}

/** `/credit-programs/new` (and `?dedicatedOrganizationId=` from an organization). */
export function CreditProgramCreateSheet() {
  usePageTitle('برنامه اعتبار اضافی جدید');
  const [params] = useSearchParams();
  const close = useCloseSheet('/credit-programs');
  const organizationId = params.get('dedicatedOrganizationId');
  return (
    <CreditProgramDialog
      {...(organizationId === null ? {} : { dedicatedOrganizationId: organizationId })}
      onOpenChange={onSheetOpenChange(close)}
    />
  );
}

// ---------------------------------------------------------------------------------------------
// The page

export function CreditProgramsPage() {
  const { pathname } = useLocation();
  usePageTitle('برنامه‌های اعتبار اضافی', pathname.replace(/\/$/, '') === '/credit-programs');
  return (
    <RequireArea area="creditPrograms">{(self) => <CreditProgramsView self={self} />}</RequireArea>
  );
}

const PROGRAM_FILTER_KEYS = ['status', 'validityMode', 'q'] as const;

function readFilters(params: URLSearchParams): CreditProgramFilters & { q?: string } {
  const filters: Record<string, string> = {};
  for (const key of PROGRAM_FILTER_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== '') filters[key] = value;
  }
  return filters as CreditProgramFilters & { q?: string };
}

function CreditProgramsView({ self }: { readonly self: AdminSelf }) {
  const [params, setParams] = useSearchParams();
  const locationSearch = useLocation().search;
  const filters = readFilters(params);
  const listFilters: CreditProgramFilters = {
    ...(filters.status === undefined ? {} : { status: filters.status }),
    ...(filters.validityMode === undefined ? {} : { validityMode: filters.validityMode }),
  };
  const programs = useCreditPrograms(listFilters, canUse(self, 'listCreditPrograms'));
  const activePrograms = useCreditPrograms(
    { status: 'ACTIVE' },
    canUse(self, 'previewCreditProgramTargeting'),
  );
  const fetched = programs.data?.pages.flatMap((page) => page.items) ?? [];
  const defaultOpen = filters.status === undefined || filters.status === '';
  const collapsed = collapseCreditProgramRows(fetched);
  const rows = collapsed
    .filter((row) => (defaultOpen ? row.status === 'ACTIVE' || row.status === 'DRAFT' : true))
    .filter((row) => matchesProgramSearch(row, filters.q ?? ''));
  const activeRows = activePrograms.data?.pages.flatMap((page) => page.items) ?? [];
  const manage = canUse(self, 'createCreditProgram');
  const canTarget = canUse(self, 'previewCreditProgramTargeting');
  const newTargetingPath =
    activeRows.length === 1
      ? creditProgramTargetingPath(activeRows[0]!.id)
      : '/credit-programs/targeting';
  const navigate = useNavigate();
  const open = (id: string) => ({ pathname: `/credit-programs/${id}`, search: locationSearch });
  const filtered =
    (filters.q !== undefined && filters.q !== '') ||
    filters.status !== undefined ||
    filters.validityMode !== undefined;

  function change(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next);
  }

  const programSearch = useSearchInput({
    applied: filters.q ?? '',
    minLength: 1,
    onApply: (q) => change({ q }),
  });
  return (
    <>
      <PageHeader
        actions={
          manage ? (
            <div className="admin-actions">
              {canTarget ? (
                <Link
                  className={buttonVariants({ variant: 'outline', className: OUTLINE_PILL })}
                  state={OPENED_FROM_LIST}
                  to={newTargetingPath}
                >
                  <Icon name="users" />
                  تخصیص جدید
                </Link>
              ) : null}
              <Link
                className={buttonVariants({ className: 'rounded-full px-5' })}
                state={OPENED_FROM_LIST}
                to="/credit-programs/new"
              >
                <Icon name="plus" />
                برنامه جدید
              </Link>
            </div>
          ) : undefined
        }
        title="برنامه‌های اعتبار"
      />
      <Section id="credit-program-list" title="فهرست برنامه‌ها">
        <div>
          <FilterPillBar
            activeFilterCount={
              [filters.q, filters.status, filters.validityMode].filter(Boolean).length
            }
            label="فیلتر برنامه‌ها"
            onClearAll={() => setParams({})}
          >
            <form
              className="shrink-0"
              onSubmit={(event) => {
                event.preventDefault();
                programSearch.submit();
              }}
            >
              <ListSearchInput
                invalid={programSearch.message !== undefined}
                label="نام یا کد برنامه"
                onChange={(event) => programSearch.setText(event.target.value)}
                placeholder="نام یا کد"
                value={programSearch.text}
              />
            </form>
            <FilterPill
              emptyLabel="فعال و پیش‌نویس"
              label="وضعیت"
              onChange={(status) => change({ status })}
              options={(
                ['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'SUPERSEDED', 'CLOSED'] as const
              ).map((value) => ({ label: statusLabel('creditProgramStatus', value), value }))}
              value={filters.status ?? ''}
            />
            <FilterPill
              emptyLabel="همه بازه‌های اعتبار"
              label="اعتبار زمانی"
              onChange={(validityMode) => change({ validityMode })}
              options={(['DURATION_FROM_ASSIGNMENT', 'FIXED_WINDOW', 'NON_EXPIRING'] as const).map(
                (value) => ({ label: statusLabel('validityMode', value), value }),
              )}
              value={filters.validityMode ?? ''}
            />
          </FilterPillBar>
          {programSearch.message === undefined ? null : (
            <p className="mb-3 text-sm text-destructive">{programSearch.message}</p>
          )}
        </div>
        <QueryState
          empty={
            <EmptyState
              action={
                manage && !filtered ? (
                  <Link
                    className={buttonVariants()}
                    state={OPENED_FROM_LIST}
                    to="/credit-programs/new"
                  >
                    تعریف اولین برنامه
                  </Link>
                ) : undefined
              }
              icon="card"
              title={filtered ? 'برنامه‌ای با این فیلترها نیست' : 'هنوز برنامه‌ای تعریف نشده است'}
            />
          }
          error={programs.error}
          isEmpty={rows.length === 0}
          onRetry={() => void programs.refetch()}
          skeleton={<Skeleton label="در حال بارگذاری برنامه‌ها…" lines={5} />}
          status={listStatus(programs)}
        >
          <AdminListTable
            caption="فهرست برنامه‌های اعتبار"
            columns={[
              {
                header: 'برنامه',
                id: 'name',
                pin: 'start',
                render: (row) => (
                  <span className="flex items-center gap-3">
                    <IconTile icon={SparklesIcon} size="sm" tone="gold" />
                    <span className="saay-cell-main">
                      <Link className="font-bold" state={OPENED_FROM_LIST} to={open(row.id)}>
                        {row.nameFa}
                      </Link>
                      <small className="text-xs">
                        <bdi dir="ltr">
                          {formatProgramCodeRevision(row.programCode, row.revision)}
                        </bdi>
                      </small>
                      {row.dedicatedOrganization === null ? null : (
                        <StatusBadge tone="forest">
                          {`اختصاصی: ${row.dedicatedOrganization.displayNameFa}`}
                        </StatusBadge>
                      )}
                    </span>
                  </span>
                ),
              },
              {
                header: 'مبلغ پیش‌فرض',
                id: 'amount',
                numeric: true,
                render: (row) => formatToman(row.defaultAmountIrr),
              },
              {
                header: 'اعتبار زمانی',
                id: 'validity',
                render: (row) => validityText(row.validityPolicy),
              },
              {
                header: 'تعهد',
                id: 'exposure',
                render: (row) => {
                  const rate =
                    row.totalExposureCapIrr === 0
                      ? 0
                      : (row.exposure.committedIrr / row.totalExposureCapIrr) * 100;
                  return (
                    <div className="admin-program-exposure">
                      <LinearProgress label={programExposureLabel(row)} value={rate} />
                      <span className="admin-program-exposure__label">
                        {programExposureLabel(row)}
                      </span>
                    </div>
                  );
                },
              },
              {
                header: 'دریافت‌کننده',
                id: 'recipients',
                numeric: true,
                render: (row) =>
                  `${formatCount(row.allocationCounts.active + row.allocationCounts.scheduled)} نفر`,
              },
              {
                header: 'وضعیت',
                id: 'status',
                render: (row) => <StatusChip enumName="creditProgramStatus" value={row.status} />,
              },
            ]}
            mobileSummary={(row) => ({
              primary: row.nameFa,
              secondary: (
                <span className="admin-inline">
                  <StatusChip enumName="creditProgramStatus" value={row.status} />
                  <span>{programExposureLabel(row)}</span>
                </span>
              ),
            })}
            onRowClick={(row) => void navigate(open(row.id), { state: OPENED_FROM_LIST })}
            {...(canTarget
              ? {
                  rowActions: (row: CreditProgram) =>
                    row.status === 'ACTIVE'
                      ? [
                          {
                            label: 'تخصیص اعتبار',
                            onSelect: () =>
                              void navigate(creditProgramTargetingPath(row.id), {
                                state: OPENED_FROM_LIST,
                              }),
                          },
                        ]
                      : [],
                }
              : {})}
            rowKey={(row) => row.id}
            rows={rows}
          />
          {programs.isFetchNextPageError ? (
            <ErrorState error={programs.error} onRetry={() => void programs.fetchNextPage()} />
          ) : null}
          <LoadMore
            hasMore={programs.hasNextPage}
            loading={programs.isFetchingNextPage}
            onLoadMore={() => void programs.fetchNextPage()}
          />
        </QueryState>
      </Section>
      <Outlet context={{ self } satisfies CreditProgramsOutletContext} />
    </>
  );
}
